import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase/client';
import type { Template } from '../types';

export const useTemplates = () => {
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    console.log('Templates: Fetching templates...');
    setLoading(true);
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      console.log('Templates: Current user:', user);
      
      if (!user) {
        console.log('Templates: No user found, skipping fetch');
        setTemplates([]);
        setLoading(false);
        return;
      }

      const { data, error } = await supabase
        .from('templates')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Templates: Error fetching:', error);
      } else {
        console.log('Templates: Fetched successfully:', data?.length || 0, 'templates');
        setTemplates(data || []);
      }
    } catch (err) {
      console.error('Templates: Exception:', err);
    } finally {
      setLoading(false);
    }
  };

  const createTemplate = async (templateData: Omit<Template, 'id' | 'created_at' | 'updated_at' | 'user_id'>) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const { data, error } = await supabase
      .from('templates')
      .insert([{ ...templateData, user_id: user.id }])
      .select()
      .single();

    if (error) throw error;
    setTemplates(prev => [data, ...prev]);
    return data;
  };

  const updateTemplate = async (id: string, updates: Partial<Template>) => {
    const { data, error } = await supabase
      .from('templates')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    setTemplates(prev => prev.map(t => t.id === id ? data : t));
    return data;
  };

  const deleteTemplate = async (id: string) => {
    const { error } = await supabase
      .from('templates')
      .delete()
      .eq('id', id);

    if (error) throw error;
    setTemplates(prev => prev.filter(t => t.id !== id));
  };

  const processTemplate = (template: Template, variables: Record<string, string>) => {
    let processed = template.content;
    
    const defaultVariables = {
      nome: variables.nome || '{nome}',
      imovel: variables.imovel || '{imovel}',
      preco: variables.preco || '{preco}',
      link: variables.link || '{link}',
      telefone: variables.telefone || '{telefone}'
    };

    for (const [key, value] of Object.entries(defaultVariables)) {
      processed = processed.replace(new RegExp(`{${key}}`, 'g'), value);
    }

    return processed;
  };

  return {
    templates,
    loading,
    createTemplate,
    updateTemplate,
    deleteTemplate,
    fetchTemplates,
    processTemplate,
  };
};
